import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { Checkbox, Form, Space, Typography } from "antd";

import { fontWeights, sizes } from "../../../../constant/styles";
import { TEXT1, TEXT3 } from "../../../../constant/colors";
import FormItemDatePicker from "./FormItemDatePicker";
import FormItemInput from "./FormItemInput";
import { priorities } from "../../../../constant/assistanceProgram";
import { useGetSetting } from "../../../../hooks/getSetting";
import { APPLICATION_RENEWAL_TIME_TYPES } from "../../../../constant/applicationRenewalTimeTypes";
import styles from "./styles";
import { dateFormatter, getCurrentDateForDatePicker } from "../../../../utils/date";
import { lookbackPeriodUnits } from "../../../../constant/potentialSavings";
import { formatToDecimal } from "../../../../utils/formaters";
import lazyLoadComponent from "../../../LazyGenerator";
import { hidesIndefiniteValidThrough } from "../../ApplicationFormEditor/templateNames";

const { Text } = Typography;

const LazyDrugPriceCalculator = lazyLoadComponent(() => import("../../../DrugPriceCalculator/DrugPriceCalculator"));

const ApprovedFields = ({ application, form, isEditing, calculateLookbackRelevanceDate }) => {
  const { t } = useTranslation();
  const [deleteDrugLocalData, setDeleteDrugLocalData] = useState(false);
  const { cellLeftPaddingStyle, cellWithBorderStyle, cellLeftPaddingBorderStyle } = styles;
  const [calculatorSetting] = useGetSetting(["fe.approved-amount-calculator-feature"]);
  const [isValidThroughDisabled, setIsValidThroughDisabled] = useState(form.getFieldValue("effectiveIndefinitely"));
  const { potentialSaving } = application;
  const hideIndefiniteValidThrough = hidesIndefiniteValidThrough(application?.ApplicationFormConfigurationTemplateName);

  useEffect(() => {
    const isEffectiveIndefinitely =
      application.effectiveIndefinitely ??
      (!application.validThrough &&
        application.potentialSaving?.applicationRenewalTimeType === APPLICATION_RENEWAL_TIME_TYPES.never);

    const validThrough =
      !application.validThrough || moment(application.validThrough).year() > 2100
        ? getValidThroughDate(potentialSaving, application.approvedDate, application.effectiveIndefinitely)
        : moment(application.validThrough);

    const approvedDate = application.approvedDate ? moment(application.approvedDate) : getCurrentDateForDatePicker();

    const approvedAmount =
      potentialSaving.grantTotal && !application.approvedAmount ? potentialSaving.grantTotal : null;

    form.setFieldsValue({
      effectiveIndefinitely: isEffectiveIndefinitely,
      validThrough,
      approvedDate,
      ...(approvedAmount ? { approvedAmount } : {})
    });

    setIsValidThroughDisabled(isEffectiveIndefinitely);
  }, [application]);

  const getLookbackRelevanceDate = () => {
    if (application.lookbackPeriodStartDate) {
      return moment(application.lookbackPeriodStartDate);
    }
    return calculateLookbackRelevanceDate();
  };

  const onConfirm = ({ newValues, potentialSavingId }) => {
    setDeleteDrugLocalData(false);
    form.setFieldsValue({
      approvedAmount: newValues.totalPrice,
      drugCalculations: newValues,
      potentialSavingId: potentialSavingId
    });
  };

  const getLookbackPeriodText = (t) => {
    if (!lookbackRelevanceTime) {
      return `${t("patientApplications.no_lookback_period")}`;
    }

    return `${
      lookbackRelevanceUnits === lookbackPeriodUnits.months
        ? t("patientApplications.x_months_lookback", {
            count: lookbackRelevanceTime
          })
        : t("patientApplications.x_days_lookback", {
            count: lookbackRelevanceTime
          })
    }`;
  };

  const showCalculator = calculatorSetting && potentialSaving.priority === priorities.FREE_DRUGS.value;
  const lookbackRelevanceTime = potentialSaving?.apLookback;
  const lookbackRelevanceUnits = potentialSaving?.lookbackRelevanceUnits;
  return (
    <Space>
      <Space style={cellWithBorderStyle}>
        <Space direction="vertical">
          <Text style={{ fontSize: sizes.medium, fontWeight: fontWeights.regular, color: TEXT3 }}>
            {t("patientApplications.approved")}
          </Text>
          {!isEditing ? (
            <Text style={{ fontSize: sizes.medium, fontWeight: fontWeights.semibold, color: TEXT1 }}>
              {dateFormatter(application.approvedDate) || "--"}
            </Text>
          ) : (
            <FormItemDatePicker
              name="approvedDate"
              onChange={(approvedDate) => {
                const updatedFieldValues = {
                  lookbackRelevanceDate: calculateLookbackRelevanceDate()
                };
                if (potentialSaving.applicationRenewalTimeType === APPLICATION_RENEWAL_TIME_TYPES.everyXMonths) {
                  updatedFieldValues.validThrough = getValidThroughDate(
                    potentialSaving,
                    approvedDate,
                    form.getFieldValue("effectiveIndefinitely")
                  );
                }
                form.setFieldsValue(updatedFieldValues);
              }}
            />
          )}
        </Space>
      </Space>
      <Space style={cellLeftPaddingBorderStyle}>
        <Space direction="vertical">
          <Text style={{ fontSize: sizes.medium, fontWeight: fontWeights.regular, color: TEXT3 }}>
            {t("patientApplications.lookback_period_starts")}
          </Text>

          {!isEditing ? (
            <Text style={{ fontSize: sizes.medium, fontWeight: fontWeights.semibold, color: TEXT1 }}>
              {application.approvedDate ? dateFormatter(getLookbackRelevanceDate()) : "--" || "--"}
            </Text>
          ) : (
            <FormItemDatePicker name="lookbackRelevanceDate" initialValue={getLookbackRelevanceDate()} />
          )}
          <Text style={{ fontSize: sizes.medium, fontWeight: fontWeights.regular, color: TEXT3 }}>
            {getLookbackPeriodText(t)}
          </Text>
        </Space>
      </Space>
      <Space style={cellLeftPaddingBorderStyle}>
        <Space direction="vertical">
          <Text style={{ fontSize: sizes.medium, fontWeight: fontWeights.regular, color: TEXT3 }}>
            {t("patientApplications.valid_through")}
          </Text>
          {!isEditing ? (
            <Text style={{ fontSize: sizes.medium, fontWeight: fontWeights.semibold, color: TEXT1 }}>
              {application.effectiveIndefinitely
                ? hideIndefiniteValidThrough
                  ? "--"
                  : t("patientApplications.indefinitely")
                : dateFormatter(application.validThrough) || "--"}
            </Text>
          ) : (
            <>
              <div>
                <FormItemDatePicker name="validThrough" disabled={isValidThroughDisabled} />
              </div>
              <Space direction="horizontal" style={{ alignItems: "flex-start" }}>
                <Form.Item name="effectiveIndefinitely" valuePropName="checked" style={{ margin: "0" }}>
                  <Checkbox
                    onChange={(e) => {
                      const newValidThroughDate = getValidThroughDate(
                        potentialSaving,
                        form.getFieldValue("approvedDate"),
                        e.target.checked
                      );
                      form.setFieldsValue({
                        validThrough: newValidThroughDate,
                        effectiveIndefinitely: e.target.checked
                      });
                      setIsValidThroughDisabled(e.target.checked);
                    }}
                  />
                </Form.Item>
                <Text style={{ fontSize: sizes.small, paddingLeft: 5 }}>
                  {t("patientApplications.valid_as_long_as")}
                </Text>
              </Space>
            </>
          )}
        </Space>
      </Space>
      <Space style={cellLeftPaddingStyle}>
        <Space direction="vertical">
          <Text style={{ fontSize: sizes.medium, fontWeight: fontWeights.regular, color: TEXT3 }}>
            {t("patientApplications.approved_amount")}
          </Text>
          {!isEditing ? (
            <Space direction="horizontal">
              {showCalculator && <LazyDrugPriceCalculator isViewOnly />}
              <Text style={{ fontSize: sizes.medium, fontWeight: fontWeights.semibold, color: TEXT1 }}>
                {application.approvedAmount ? `$${formatToDecimal(application.approvedAmount)}` : "$0"}
              </Text>
            </Space>
          ) : (
            <Space direction="horizontal">
              <FormItemInput
                controls={false}
                name="approvedAmount"
                isNumber
                rules={[{ required: true, message: t("patientApplications.approved_amount_required") }]}
                onChange={() => setDeleteDrugLocalData(true)}
              />
              {showCalculator && (
                <LazyDrugPriceCalculator
                  onConfirm={onConfirm}
                  deleteLocalData={deleteDrugLocalData}
                  shouldUpdate={false}
                />
              )}
            </Space>
          )}
        </Space>
      </Space>
    </Space>
  );
};

export function getValidThroughDate(potentialSaving, approvedDate, effectiveIndefinitely) {
  if (effectiveIndefinitely) {
    return null;
  }
  const { applicationRenewalTimeType: renewalTimeType, applicationRenewalTimeInMonths: renewalTimeInMonths } =
    potentialSaving;
  const calculatedApprovedDate = approvedDate || moment();
  const validThroughDateGenerator = {
    endOfCurrentYear: (approvedDate) => moment(approvedDate).endOf("year").startOf("day"),
    everyXMonths: (approvedDate, months) => moment(approvedDate).add(months || 0, "month"),
    never: () => null
  };
  let newValidThroughDate;
  switch (renewalTimeType) {
    case APPLICATION_RENEWAL_TIME_TYPES.everyXMonths:
      newValidThroughDate = validThroughDateGenerator.everyXMonths(calculatedApprovedDate, renewalTimeInMonths);
      break;
    case APPLICATION_RENEWAL_TIME_TYPES.endOfCurrentYear:
      newValidThroughDate = validThroughDateGenerator.endOfCurrentYear(calculatedApprovedDate);
      break;
    default:
      newValidThroughDate = validThroughDateGenerator.never();
      break;
  }
  return newValidThroughDate;
}

export default ApprovedFields;
